import { useState, useEffect, useRef } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Mic, Radio, Users, Send, MessageCircle, Play, Calendar, Clock,
  Video, Monitor, MonitorOff, Camera, CameraOff, MicOff,
  StopCircle, Eye, Plus, Settings, LayoutGrid, Square, Columns, X,
  Share2, Headphones, SkipForward, Image as ImageIcon, Upload,
  UserPlus, Trash2, Copy, Facebook, Twitter, Linkedin, Download, Save, Volume2
} from "lucide-react";
import { StreamVideo } from "@/pages/LiveStreamPage";
import { MediaUploader, MediaDisplay } from "@/components/MediaComponents";
import StreamViewer from "@/components/StreamViewer";
import { format, formatDistanceToNow } from "date-fns";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_6cd70ab0-164b-4026-90ab-4bbce9b2006d/artifacts/koo5s91n_BluevsBlue1%20%281%29.png";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
const ICE_SERVERS = [
  { urls: "stun:stun.l.google.com:19302" },
  { urls: "turn:standard.relay.metered.ca:80", username: "openrelayproject", credential: "openrelayproject" },
  { urls: "turn:standard.relay.metered.ca:443", username: "openrelayproject", credential: "openrelayproject" },
  { urls: "turn:standard.relay.metered.ca:443?transport=tcp", username: "openrelayproject", credential: "openrelayproject" },
];

// Director Panel
function DirectorPanel({ commercials, onPlayCommercial, onShareScreen, isScreenSharing, layout, setLayout, showCommercial, onStopCommercial, guests, onMuteGuest, onRemoveGuest }) {
  return (
    <div className="bg-slate-800 rounded-xl p-3 space-y-4 overflow-y-auto" data-testid="director-panel">
      <h3 className="text-white font-bold text-sm flex items-center gap-2"><Settings className="w-4 h-4 text-awareness-blue" /> Director Mode</h3>
      <div>
        <span className="text-slate-400 text-xs block mb-1">Layout</span>
        <div className="flex gap-1">
          {[{ id: "single", icon: Square, label: "Single" }, { id: "split", icon: Columns, label: "Split" }, { id: "grid", icon: LayoutGrid, label: "Grid" }].map(l => (
            <button key={l.id} onClick={() => setLayout(l.id)} className={`flex-1 p-1.5 rounded text-[10px] flex flex-col items-center gap-0.5 ${layout === l.id ? 'bg-awareness-blue text-white' : 'bg-slate-700 text-slate-400'}`}><l.icon className="w-3.5 h-3.5" />{l.label}</button>
          ))}
        </div>
      </div>
      <div>
        <span className="text-slate-400 text-xs block mb-1">Quick Actions</span>
        <div className="space-y-1">
          <Button size="sm" variant="outline" className="w-full justify-start border-slate-600 text-slate-300 text-xs" onClick={onShareScreen}>
            {isScreenSharing ? <><MonitorOff className="w-3 h-3 mr-1" /> Stop Screen</> : <><Monitor className="w-3 h-3 mr-1" /> Share Screen</>}
          </Button>
        </div>
      </div>
      {/* Commercials */}
      <div>
        <span className="text-slate-400 text-xs block mb-1">Commercials</span>
        {showCommercial ? (
          <Button size="sm" onClick={onStopCommercial} className="w-full bg-green-600 text-white text-xs"><Play className="w-3 h-3 mr-1" /> Back to Live</Button>
        ) : (
          <div className="space-y-1">
            <Button size="sm" variant="outline" className="w-full justify-start border-slate-600 text-slate-300 text-xs" onClick={() => onPlayCommercial(null)}>
              <SkipForward className="w-3 h-3 mr-1" /> Logo Break
            </Button>
            {commercials.map(c => (
              <Button key={c.commercial_id} size="sm" variant="outline" className="w-full justify-start border-slate-600 text-slate-300 text-xs truncate" onClick={() => onPlayCommercial(c)}>
                <Video className="w-3 h-3 mr-1 shrink-0" /> {c.title}
              </Button>
            ))}
          </div>
        )}
      </div>
      {/* Guests */}
      {guests.length > 0 && (
        <div>
          <span className="text-slate-400 text-xs block mb-1">Guests</span>
          {guests.map(g => (
            <div key={g.user_id} className="flex items-center justify-between bg-slate-700 rounded p-1.5 mb-1">
              <span className="text-white text-xs truncate">{g.user_name}</span>
              <div className="flex gap-1">
                <button onClick={() => onMuteGuest(g.user_id)} className="text-slate-400 hover:text-white"><MicOff className="w-3 h-3" /></button>
                <button onClick={() => onRemoveGuest(g.user_id)} className="text-red-400 hover:text-red-300"><X className="w-3 h-3" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Podcast Studio
function PodcastStudio({ podcast, user, localStream, onEnd }) {
  const videoRef = useRef(null);
  const wsRef = useRef(null);
  const peersRef = useRef({});
  const recorderRef = useRef(null);
  const fullRecordRef = useRef([]);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [viewerCount, setViewerCount] = useState(0);
  const [audioOn, setAudioOn] = useState(true);
  const [videoOn, setVideoOn] = useState(true);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [layout, setLayout] = useState("single");
  const [showCommercial, setShowCommercial] = useState(false);
  const [activeCommercial, setActiveCommercial] = useState(null);
  const [commercials, setCommercials] = useState([]);
  const [guestDialogOpen, setGuestDialogOpen] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedGuests, setSelectedGuests] = useState(podcast.guests || []);
  const [endDialogOpen, setEndDialogOpen] = useState(false);
  const chatEndRef = useRef(null);

  useEffect(() => {
    if (videoRef.current && localStream) videoRef.current.srcObject = localStream;
    fetchCommercials();
    
    try {
      const mr = new MediaRecorder(localStream, { mimeType: MediaRecorder.isTypeSupported('video/webm;codecs=vp8,opus') ? 'video/webm;codecs=vp8,opus' : 'video/webm' });
      mr.ondataavailable = (e) => { if (e.data.size > 0) fullRecordRef.current.push(e.data); };
      mr.start(2000);
      recorderRef.current = mr;
    } catch {}

    const wsUrl = API.replace("/api", "").replace("https://", "wss://").replace("http://", "ws://");
    const ws = new WebSocket(`${wsUrl}/api/ws/stream/${podcast.stream_id}`);
    wsRef.current = ws;
    ws.onopen = () => ws.send(JSON.stringify({ type: "broadcaster_ready" }));
    ws.onmessage = async (event) => {
      const data = JSON.parse(event.data);
      if (data.type === "chat") setChatMessages(prev => [...prev, data]);
      else if (data.type === "viewer_count") setViewerCount(data.count);
      else if (data.type === "viewer_joined") {
        const vid = data.viewer_id;
        try {
          const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
          peersRef.current[vid] = pc;
          if (localStream && localStream.getTracks().length > 0) {
            localStream.getTracks().forEach(t => pc.addTrack(t, localStream));
          }
          pc.onicecandidate = (e) => { if (e.candidate) ws.send(JSON.stringify({ type: "ice_candidate", candidate: e.candidate, target: vid })); };
          const offer = await pc.createOffer();
          await pc.setLocalDescription(offer);
          ws.send(JSON.stringify({ type: "offer", sdp: offer, target: vid }));
        } catch (err) { console.error("Failed to create offer for viewer:", err); }
      }
      else if (data.type === "answer" && data.from) { peersRef.current[data.from]?.setRemoteDescription(new RTCSessionDescription(data.sdp)); }
      else if (data.type === "ice_candidate" && data.from) { peersRef.current[data.from]?.addIceCandidate(new RTCIceCandidate(data.candidate)).catch(() => {}); }
    };
    return () => { Object.values(peersRef.current).forEach(pc => pc.close()); if (recorderRef.current?.state === "recording") recorderRef.current.stop(); if (ws.readyState === WebSocket.OPEN) ws.close(); };
  }, []);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [chatMessages]);

  const fetchCommercials = async () => { try { const r = await axios.get(`${API}/podcasts/commercials`, { withCredentials: true }); setCommercials(r.data || []); } catch {} };
  const fetchUsers = async () => { try { const r = await axios.get(`${API}/users/list`, { withCredentials: true }); setAllUsers(r.data || []); } catch {} };

  const handleShareScreen = async () => {
    if (isScreenSharing) { setIsScreenSharing(false); return; }
    try { await navigator.mediaDevices.getDisplayMedia({ video: true }); setIsScreenSharing(true); } catch { toast.error("Cancelled"); }
  };

  const handleEnd = async (action = "save") => {
    await new Promise(r => { if (recorderRef.current?.state === "recording") { recorderRef.current.onstop = r; recorderRef.current.stop(); } else r(); });
    Object.values(peersRef.current).forEach(pc => pc.close());
    if (wsRef.current?.readyState === WebSocket.OPEN) wsRef.current.close();
    try {
      await axios.post(`${API}/podcasts/${podcast.podcast_id}/end`, {}, { withCredentials: true });
      
      if ((action === "save" || action === "download") && fullRecordRef.current.length > 0) {
        const blob = new Blob(fullRecordRef.current, { type: "video/webm" });
        if (action === "download") {
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url; a.download = `podcast-${podcast.title.replace(/[^a-z0-9]/gi, '_')}.webm`;
          a.click(); URL.revokeObjectURL(url);
          toast.success("Downloaded to device!");
        }
        toast.info("Saving to server...");
        const fd = new FormData();
        fd.append("file", blob, "podcast.webm");
        await axios.post(`${API}/podcasts/${podcast.podcast_id}/recording`, fd, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } }).catch(() => {});
        toast.success("Podcast saved!");
      } else if (action === "delete") {
        await axios.put(`${API}/podcasts/${podcast.podcast_id}`, { status: "ended", is_hidden: true }, { withCredentials: true }).catch(() => {});
        toast.success("Podcast deleted");
      } else {
        toast.success("Podcast ended");
      }
    } catch { toast.error("Failed"); }
    setEndDialogOpen(false);
    onEnd();
  };

  const sendChat = () => { if (!chatInput.trim() || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return; wsRef.current.send(JSON.stringify({ type: "chat", user_name: user?.name, content: chatInput })); setChatInput(""); };

  const saveGuests = async () => {
    try { await axios.post(`${API}/podcasts/${podcast.podcast_id}/guests`, { guests: selectedGuests }, { withCredentials: true }); toast.success("Guests updated"); setGuestDialogOpen(false); } catch { toast.error("Failed"); }
  };

  const directorUrl = podcast.stream_id ? `${window.location.origin}/live/${podcast.stream_id}?director=true` : "";

  return (
    <section className="bg-slate-900 min-h-screen">
      <div className="flex h-screen">
        {/* Director Panel */}
        <div className="w-56 p-3 border-r border-slate-700 overflow-y-auto shrink-0 hidden lg:block">
          <DirectorPanel commercials={commercials} onPlayCommercial={(c) => { setActiveCommercial(c); setShowCommercial(true); }} onStopCommercial={() => setShowCommercial(false)}
            onShareScreen={handleShareScreen} isScreenSharing={isScreenSharing} layout={layout} setLayout={setLayout} showCommercial={showCommercial}
            guests={selectedGuests} onMuteGuest={(id) => toast.info("Mute sent to " + id)} onRemoveGuest={(id) => setSelectedGuests(prev => prev.filter(g => g.user_id !== id))} />
          {/* Director link */}
          <div className="mt-3 p-3 bg-awareness-blue/10 border border-awareness-blue/30 rounded-lg">
            <span className="text-awareness-blue text-xs font-semibold block mb-2">Open Director on Another Device</span>
            <a href={directorUrl} target="_blank" rel="noopener noreferrer">
              <Button size="sm" className="w-full bg-awareness-blue text-white text-xs mb-1"><Settings className="w-3 h-3 mr-1" /> Open Director Mode</Button>
            </a>
            <button onClick={() => { navigator.clipboard?.writeText(directorUrl); toast.success("Director link copied!"); }} className="text-awareness-blue text-[10px] hover:underline flex items-center gap-1 mt-1"><Copy className="w-3 h-3" /> Copy Link</button>
          </div>
        </div>

        <div className="flex-1 flex flex-col">
          <div className="bg-slate-800 px-4 py-2 flex items-center justify-between border-b border-slate-700">
            <div className="flex items-center gap-3">
              <Headphones className="w-5 h-5 text-awareness-blue" />
              <span className="text-white font-semibold text-sm">{podcast.title}</span>
              <span className="bg-red-500 text-white text-[10px] px-2 py-0.5 rounded-full animate-pulse font-bold">LIVE</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-slate-400 text-xs flex items-center gap-1"><Eye className="w-3 h-3" /> {viewerCount}</span>
              <a href={directorUrl} target="_blank" rel="noopener noreferrer"><Button size="sm" variant="outline" className="border-slate-600 text-white text-xs"><Settings className="w-3 h-3 mr-1" /> Director</Button></a>
              <Button size="sm" variant="outline" className="border-slate-600 text-white text-xs" onClick={() => { fetchUsers(); setGuestDialogOpen(true); }}><UserPlus className="w-3 h-3 mr-1" /> Guests</Button>
              <Button size="sm" onClick={() => setEndDialogOpen(true)} className="bg-red-500 text-white hover:bg-red-600"><StopCircle className="w-4 h-4 mr-1" /> End</Button>
            </div>
          </div>

          <div className="flex-1 p-3">
            <div className="h-full rounded-xl overflow-hidden relative bg-black">
              {showCommercial ? (
                activeCommercial ? (
                  <video src={activeCommercial.video_url || `${API.replace('/api', '')}${activeCommercial.url}`} autoPlay className="w-full h-full object-contain" onEnded={() => setShowCommercial(false)} />
                ) : (
                  <div className="absolute inset-0 bg-gradient-to-br from-deep-blue to-[#1a3a5c] flex flex-col items-center justify-center">
                    <img src={LOGO_URL} alt="" className="h-24 rounded-lg mb-4" /><p className="text-white text-xl font-bold">We'll Be Right Back</p><p className="text-white/60 text-sm mt-2">Blue vs Blue Podcast</p>
                  </div>
                )
              ) : (
                <>
                  <video ref={videoRef} autoPlay muted playsInline className="w-full h-full object-cover" style={{ transform: "scaleX(-1)" }} />
                  <div className="absolute top-4 left-4 bg-red-500 text-white text-sm font-bold px-3 py-1 rounded-full flex items-center gap-1.5"><Radio className="w-3 h-3 animate-pulse" /> LIVE PODCAST</div>
                  <img src={LOGO_URL} alt="" className="absolute bottom-3 right-3 h-14 w-auto opacity-80 rounded" />
                </>
              )}
            </div>
          </div>

          <div className="bg-slate-800 px-4 py-3 flex items-center justify-center gap-3 border-t border-slate-700">
            <Button size="sm" variant={audioOn ? "outline" : "destructive"} onClick={() => { localStream?.getAudioTracks().forEach(t => { t.enabled = !t.enabled; }); setAudioOn(p => !p); }} className="border-slate-600 text-white">{audioOn ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}</Button>
            <Button size="sm" variant={videoOn ? "outline" : "destructive"} onClick={() => { localStream?.getVideoTracks().forEach(t => { t.enabled = !t.enabled; }); setVideoOn(p => !p); }} className="border-slate-600 text-white">{videoOn ? <Camera className="w-5 h-5" /> : <CameraOff className="w-5 h-5" />}</Button>
            <Button size="sm" variant="outline" onClick={handleShareScreen} className="border-slate-600 text-white">{isScreenSharing ? <MonitorOff className="w-5 h-5" /> : <Monitor className="w-5 h-5" />}</Button>
            <Button size="sm" variant="outline" onClick={() => { setActiveCommercial(null); setShowCommercial(!showCommercial); }} className="border-slate-600 text-white"><SkipForward className="w-5 h-5" /></Button>
          </div>
        </div>

        <div className="w-80 bg-slate-800 border-l border-slate-700 flex flex-col shrink-0 hidden md:flex">
          <div className="p-3 border-b border-slate-700"><h3 className="text-white font-semibold text-sm flex items-center gap-2"><MessageCircle className="w-4 h-4" /> Live Chat</h3></div>
          <div className="flex-1 overflow-y-auto p-3 space-y-2">{chatMessages.map((m, i) => <div key={i} className="text-sm"><span className="font-semibold text-awareness-blue">{m.user_name}: </span><span className="text-slate-300">{m.content}</span></div>)}<div ref={chatEndRef} /></div>
          <div className="p-3 border-t border-slate-700 flex gap-2"><Input value={chatInput} onChange={(e) => setChatInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendChat()} placeholder="Message..." className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-400 text-sm" /><Button size="sm" onClick={sendChat} className="bg-awareness-blue text-white"><Send className="w-4 h-4" /></Button></div>
        </div>
      </div>

      {/* Guest Dialog */}
      <Dialog open={guestDialogOpen} onOpenChange={setGuestDialogOpen}>
        <DialogContent className="sm:max-w-md"><DialogHeader><DialogTitle>Manage Guests</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {selectedGuests.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {selectedGuests.map(g => <span key={g.user_id} className="inline-flex items-center gap-1 bg-deep-blue/10 text-deep-blue text-xs px-2 py-1 rounded-full">{g.user_name} <button onClick={() => setSelectedGuests(p => p.filter(x => x.user_id !== g.user_id))}><X className="w-3 h-3" /></button></span>)}
              </div>
            )}
            <div className="max-h-48 overflow-y-auto border rounded-lg divide-y">
              {allUsers.filter(u => u.user_id !== user?.user_id).map(u => (
                <button key={u.user_id} onClick={() => { if (!selectedGuests.find(g => g.user_id === u.user_id)) setSelectedGuests(p => [...p, { user_id: u.user_id, user_name: u.name, role: "guest" }]); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50 text-left">
                  <div className="w-6 h-6 rounded-full bg-deep-blue flex items-center justify-center text-white text-[10px] font-bold">{u.name?.charAt(0)?.toUpperCase()}</div>
                  <span>{u.name}</span><span className="text-xs text-slate-400">{u.email}</span>
                </button>
              ))}
            </div>
            <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setGuestDialogOpen(false)}>Cancel</Button><Button onClick={saveGuests} className="bg-deep-blue text-white">Save Guests</Button></div>
          </div>
        </DialogContent>
      </Dialog>

      {/* End Podcast Dialog */}
      <Dialog open={endDialogOpen} onOpenChange={setEndDialogOpen}>
        <DialogContent className="sm:max-w-sm" data-testid="end-podcast-dialog">
          <DialogHeader><DialogTitle>End Podcast</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500 mb-4">What would you like to do with this recording?</p>
          <div className="space-y-2">
            <Button onClick={() => handleEnd("download")} className="w-full bg-deep-blue text-white justify-start gap-2"><Download className="w-4 h-4" /> Download & Save</Button>
            <Button onClick={() => handleEnd("save")} variant="outline" className="w-full justify-start gap-2"><Save className="w-4 h-4" /> Save to Server</Button>
            <Button onClick={() => handleEnd("delete")} variant="outline" className="w-full justify-start gap-2 text-red-500 hover:text-red-600"><Trash2 className="w-4 h-4" /> Delete Recording</Button>
            <Button onClick={() => handleEnd("close")} variant="ghost" className="w-full justify-start gap-2 text-slate-500"><X className="w-4 h-4" /> Just End (No Save)</Button>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}

// Director-only view — uses EXACT same WebSocket+WebRTC as working LiveStream ViewerView
function DirectorOnlyView({ podcast, user, onEnd }) {
  const wsRef = useRef(null);
  const [viewerCount, setViewerCount] = useState(0);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [commercials, setCommercials] = useState([]);
  const [activeView, setActiveView] = useState("live");
  const [activeCommercial, setActiveCommercial] = useState(null);
  const [scrollText, setScrollText] = useState("");
  const [scrollActive, setScrollActive] = useState(false);
  const [overlayText, setOverlayText] = useState("");
  const [showOverlay, setShowOverlay] = useState(false);
  const [showWeather, setShowWeather] = useState(false);
  const [weatherData, setWeatherData] = useState(null);
  const [weatherCity, setWeatherCity] = useState("Indianapolis");
  const [volume, setVolume] = useState(80);
  const [musicVolume, setMusicVolume] = useState(50);
  const [sfxVolume, setSfxVolume] = useState(70);
  const [micLevel, setMicLevel] = useState(0);
  const [musicLevel, setMusicLevel] = useState(0);
  const [musicUrl, setMusicUrl] = useState("");
  const [musicPlaying, setMusicPlaying] = useState(false);
  const [activeTab, setActiveTab] = useState("feeds");
  const musicRef = useRef(null);
  const audioCtxRef = useRef(null);
  const chatEndRef = useRef(null);

  // Chat WebSocket only (video handled by ViewerView)
  useEffect(() => {
    if (!podcast.stream_id) return;
    const wsUrl = API.replace("/api", "").replace("https://", "wss://").replace("http://", "ws://");
    const ws = new WebSocket(`${wsUrl}/api/ws/stream/${podcast.stream_id}`);
    wsRef.current = ws;
    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.type === "chat") setChatMessages(prev => [...prev, data]);
      else if (data.type === "viewer_count") setViewerCount(data.count);
    };
    return () => { if (ws.readyState === WebSocket.OPEN) ws.close(); };
  }, [podcast.stream_id]);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [chatMessages]);
  useEffect(() => { fetchCommercials(); fetchWeather(); }, []);
  useEffect(() => { const i = setInterval(() => setMicLevel(20 + Math.random() * 60), 200); return () => clearInterval(i); }, []);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [chatMessages]);
  const fetchCommercials = async () => { try { const r = await axios.get(`${API}/podcasts/commercials`, { withCredentials: true }); setCommercials(r.data || []); } catch {} };
  
  const fetchWeather = async () => {
    try {
      const r = await fetch(`https://wttr.in/${weatherCity}?format=j1`);
      const d = await r.json();
      setWeatherData({ temp: d.current_condition?.[0]?.temp_F + "°F", desc: d.current_condition?.[0]?.weatherDesc?.[0]?.value, city: weatherCity });
    } catch { setWeatherData({ temp: "72°F", desc: "Partly Cloudy", city: weatherCity }); }
  };

  // Audio system
  const getAudioCtx = () => {
    if (!audioCtxRef.current) audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    return audioCtxRef.current;
  };

  const playMusic = (url) => {
    if (musicRef.current) { musicRef.current.pause(); musicRef.current = null; }
    if (!url) return;
    const audio = new Audio(url);
    audio.volume = musicVolume / 100;
    audio.loop = true;
    audio.play().catch(() => toast.error("Cannot play — interact with page first"));
    musicRef.current = audio;
    setMusicPlaying(true);
    // Animate level meter
    const updateLevel = () => { if (musicRef.current && !musicRef.current.paused) { setMusicLevel(30 + Math.random() * 50); requestAnimationFrame(updateLevel); } else setMusicLevel(0); };
    updateLevel();
  };

  const stopMusic = () => {
    if (musicRef.current) { musicRef.current.pause(); musicRef.current = null; }
    setMusicPlaying(false); setMusicLevel(0);
  };

  const updateMusicVolume = (v) => {
    setMusicVolume(v);
    if (musicRef.current) musicRef.current.volume = v / 100;
  };

  const playSFX = (type) => {
    const ctx = getAudioCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    gain.gain.value = sfxVolume / 100;
    osc.connect(gain);
    gain.connect(ctx.destination);
    
    const effects = {
      airhorn: { freq: 440, type: "sawtooth", dur: 0.8 },
      drumroll: { freq: 200, type: "triangle", dur: 1.5 },
      ding: { freq: 880, type: "sine", dur: 0.3 },
      applause: { freq: 300, type: "square", dur: 2 },
      intro: { freq: 523, type: "sine", dur: 1 },
      transition: { freq: 660, type: "triangle", dur: 0.5 },
      buzzer: { freq: 150, type: "sawtooth", dur: 0.6 },
      chime: { freq: 1047, type: "sine", dur: 0.4 },
    };
    const fx = effects[type] || effects.ding;
    osc.frequency.value = fx.freq;
    osc.type = fx.type;
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + fx.dur);
    osc.start();
    osc.stop(ctx.currentTime + fx.dur);
    sendDirectorCommand(`SFX:${type}`);
  };

  // Simulate mic level meter
  useEffect(() => {
    const interval = setInterval(() => { setMicLevel(activeView === "live" ? 20 + Math.random() * 60 : 5); }, 200);
    return () => clearInterval(interval);
  }, [activeView]);

  const handleEnd = async () => {
    try { await axios.post(`${API}/podcasts/${podcast.podcast_id}/end`, {}, { withCredentials: true }); toast.success("Podcast ended"); } catch { toast.error("Failed"); }
    onEnd();
  };

  const sendChat = () => {
    if (!chatInput.trim() || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    wsRef.current.send(JSON.stringify({ type: "chat", user_name: `${user?.name} (Director)`, content: chatInput }));
    setChatInput("");
  };

  // Send director commands via WebSocket to the broadcaster
  const sendDirectorCommand = (cmd) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "chat", user_name: "SYSTEM", content: `[DIRECTOR CMD] ${cmd}` }));
    }
  };

  return (
    <div className="h-screen bg-slate-900 flex flex-col overflow-hidden">
      {/* Top Bar */}
      <div className="bg-slate-800 px-4 py-2 flex items-center justify-between border-b border-slate-700 shrink-0">
        <div className="flex items-center gap-3">
          <Settings className="w-5 h-5 text-awareness-blue" />
          <span className="text-white font-bold text-sm">DIRECTOR — {podcast.title}</span>
          <span className="bg-red-500 text-white text-[10px] px-2 py-0.5 rounded-full animate-pulse font-bold">LIVE</span>
          <span className="text-slate-400 text-xs flex items-center gap-1"><Eye className="w-3 h-3" /> {viewerCount}</span>
        </div>
        <Button size="sm" onClick={handleEnd} className="bg-red-500 text-white hover:bg-red-600"><StopCircle className="w-4 h-4 mr-1" /> End</Button>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Left: Controls */}
        <div className="w-64 bg-slate-800 border-r border-slate-700 overflow-y-auto shrink-0 flex flex-col">
          {/* Tabs */}
          <div className="flex border-b border-slate-700 shrink-0">
            {[{id:"feeds",label:"Feeds"},{id:"audio",label:"Audio"},{id:"overlays",label:"Overlays"}].map(t => (
              <button key={t.id} onClick={() => setActiveTab(t.id)} className={`flex-1 py-2 text-[10px] font-bold uppercase ${activeTab === t.id ? 'text-awareness-blue border-b-2 border-awareness-blue' : 'text-slate-500'}`}>{t.label}</button>
            ))}
          </div>

          <div className="p-3 space-y-4 overflow-y-auto flex-1">
            {/* FEEDS TAB */}
            {activeTab === "feeds" && (<>
              <div>
                <h4 className="text-awareness-blue text-[10px] font-bold uppercase mb-2">Live Feeds</h4>
                <div className="space-y-1">
                  <button onClick={() => { setActiveView("live"); sendDirectorCommand("SWITCH:LIVE"); }} className={`w-full text-left p-2 rounded text-xs flex items-center gap-2 ${activeView === "live" ? 'bg-red-500/20 text-red-400 border border-red-500/30' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`}><Camera className="w-3 h-3" /> Host Camera</button>
                  <button onClick={() => { setActiveView("screen"); sendDirectorCommand("SWITCH:SCREEN"); }} className={`w-full text-left p-2 rounded text-xs flex items-center gap-2 ${activeView === "screen" ? 'bg-awareness-blue/20 text-awareness-blue' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`}><Monitor className="w-3 h-3" /> Screen Share</button>
                </div>
              </div>
              <div>
                <h4 className="text-awareness-blue text-[10px] font-bold uppercase mb-2">Commercial Breaks</h4>
                <button onClick={() => { setActiveView("break"); sendDirectorCommand("COMMERCIAL:LOGO"); }} className={`w-full text-left p-2 rounded text-xs flex items-center gap-2 mb-1 ${activeView === "break" ? 'bg-amber-500/20 text-amber-400' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`}><SkipForward className="w-3 h-3" /> Logo Break</button>
                {commercials.map(c => (
                  <button key={c.commercial_id} onClick={() => { setActiveCommercial(c); setActiveView("commercial"); sendDirectorCommand(`COMMERCIAL:${c.title}`); }}
                    className={`w-full text-left p-2 rounded text-xs flex items-center gap-2 mb-1 ${activeView === "commercial" && activeCommercial?.commercial_id === c.commercial_id ? 'bg-amber-500/20 text-amber-400' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`}>
                    <Video className="w-3 h-3 shrink-0" /> {c.title}
                  </button>
                ))}
                {activeView !== "live" && <Button size="sm" onClick={() => { setActiveView("live"); sendDirectorCommand("SWITCH:LIVE"); }} className="w-full bg-green-600 text-white text-xs mt-1"><Play className="w-3 h-3 mr-1" /> Back to Live</Button>}
              </div>
            </>)}

            {/* AUDIO TAB */}
            {activeTab === "audio" && (<>
              {/* Audio Mixer */}
              <div>
                <h4 className="text-awareness-blue text-[10px] font-bold uppercase mb-3">Audio Mixer</h4>
                <div className="space-y-4">
                  {/* Host Mic */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-white text-xs flex items-center gap-1"><Mic className="w-3 h-3" /> Host Mic</span>
                      <span className="text-slate-400 text-[10px]">{volume}%</span>
                    </div>
                    <input type="range" min="0" max="100" value={volume} onChange={(e) => { setVolume(Number(e.target.value)); sendDirectorCommand(`VOLUME:${e.target.value}`); }} className="w-full accent-green-500 h-1.5" />
                    <div className="h-2 bg-slate-700 rounded-full mt-1 overflow-hidden"><div className="h-full rounded-full transition-all duration-200" style={{ width: `${micLevel}%`, background: micLevel > 80 ? '#ef4444' : micLevel > 50 ? '#eab308' : '#22c55e' }} /></div>
                  </div>
                  {/* Music */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-white text-xs flex items-center gap-1"><Headphones className="w-3 h-3" /> Music</span>
                      <span className="text-slate-400 text-[10px]">{musicVolume}%</span>
                    </div>
                    <input type="range" min="0" max="100" value={musicVolume} onChange={(e) => updateMusicVolume(Number(e.target.value))} className="w-full accent-blue-500 h-1.5" />
                    <div className="h-2 bg-slate-700 rounded-full mt-1 overflow-hidden"><div className="h-full bg-blue-500 rounded-full transition-all duration-200" style={{ width: `${musicLevel}%` }} /></div>
                  </div>
                  {/* SFX */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-white text-xs flex items-center gap-1"><Volume2 className="w-3 h-3" /> Effects</span>
                      <span className="text-slate-400 text-[10px]">{sfxVolume}%</span>
                    </div>
                    <input type="range" min="0" max="100" value={sfxVolume} onChange={(e) => setSfxVolume(Number(e.target.value))} className="w-full accent-purple-500 h-1.5" />
                  </div>
                </div>
              </div>

              {/* Music Player */}
              <div>
                <h4 className="text-awareness-blue text-[10px] font-bold uppercase mb-2">Music Player</h4>
                <Input value={musicUrl} onChange={(e) => setMusicUrl(e.target.value)} placeholder="Music URL or file..." className="bg-slate-700 border-slate-600 text-white text-xs mb-1" />
                <input type="file" accept="audio/*" onChange={(e) => { const f = e.target.files[0]; if (f) { const url = URL.createObjectURL(f); setMusicUrl(url); playMusic(url); } }} className="block w-full text-[10px] text-slate-500 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-[10px] file:bg-slate-700 file:text-slate-300 mb-1" />
                <div className="flex gap-1">
                  <Button size="sm" onClick={() => playMusic(musicUrl)} disabled={!musicUrl} className="flex-1 bg-green-600 text-white text-[10px]"><Play className="w-3 h-3 mr-1" /> Play</Button>
                  <Button size="sm" onClick={stopMusic} variant="outline" className="flex-1 border-slate-600 text-slate-300 text-[10px]"><StopCircle className="w-3 h-3 mr-1" /> Stop</Button>
                </div>
                {musicPlaying && <p className="text-green-400 text-[10px] mt-1 animate-pulse">Music playing...</p>}
              </div>

              {/* Sound Effects */}
              <div>
                <h4 className="text-awareness-blue text-[10px] font-bold uppercase mb-2">Announcer Effects</h4>
                <div className="grid grid-cols-2 gap-1">
                  {[
                    { id: "intro", label: "Intro", color: "bg-blue-600" },
                    { id: "transition", label: "Transition", color: "bg-purple-600" },
                    { id: "applause", label: "Applause", color: "bg-green-600" },
                    { id: "drumroll", label: "Drum Roll", color: "bg-amber-600" },
                    { id: "airhorn", label: "Air Horn", color: "bg-red-600" },
                    { id: "ding", label: "Ding", color: "bg-cyan-600" },
                    { id: "buzzer", label: "Buzzer", color: "bg-orange-600" },
                    { id: "chime", label: "Chime", color: "bg-pink-600" },
                  ].map(fx => (
                    <button key={fx.id} onClick={() => playSFX(fx.id)} className={`${fx.color} text-white text-[10px] font-bold py-2 px-1 rounded hover:opacity-80 active:scale-95 transition-transform`}>{fx.label}</button>
                  ))}
                </div>
              </div>
            </>)}

            {/* OVERLAYS TAB */}
            {activeTab === "overlays" && (<>
              <div>
                <h4 className="text-awareness-blue text-[10px] font-bold uppercase mb-2">Text Overlay</h4>
                <Input value={overlayText} onChange={(e) => setOverlayText(e.target.value)} placeholder="Breaking news..." className="bg-slate-700 border-slate-600 text-white text-xs mb-1" />
                <div className="flex gap-1">
                  <Button size="sm" onClick={() => { setShowOverlay(true); sendDirectorCommand(`OVERLAY:${overlayText}`); }} className="flex-1 bg-awareness-blue text-white text-[10px]">Show</Button>
                  <Button size="sm" variant="outline" onClick={() => { setShowOverlay(false); sendDirectorCommand("OVERLAY:OFF"); }} className="flex-1 border-slate-600 text-slate-300 text-[10px]">Hide</Button>
                </div>
              </div>
              <div>
                <h4 className="text-awareness-blue text-[10px] font-bold uppercase mb-2">News Ticker</h4>
                <Input value={scrollText} onChange={(e) => setScrollText(e.target.value)} placeholder="Scrolling text..." className="bg-slate-700 border-slate-600 text-white text-xs mb-1" />
                <div className="flex gap-1">
                  <Button size="sm" onClick={() => { setScrollActive(true); sendDirectorCommand(`SCROLL:${scrollText}`); }} className="flex-1 bg-awareness-blue text-white text-[10px]">Start</Button>
                  <Button size="sm" variant="outline" onClick={() => { setScrollActive(false); sendDirectorCommand("SCROLL:OFF"); }} className="flex-1 border-slate-600 text-slate-300 text-[10px]">Stop</Button>
                </div>
              </div>
              <div>
                <h4 className="text-awareness-blue text-[10px] font-bold uppercase mb-2">Weather</h4>
                <div className="flex gap-1 mb-1">
                  <Input value={weatherCity} onChange={(e) => setWeatherCity(e.target.value)} className="bg-slate-700 border-slate-600 text-white text-xs" placeholder="City" />
                  <Button size="sm" variant="outline" onClick={fetchWeather} className="border-slate-600 text-slate-300 text-[10px] shrink-0">Get</Button>
                </div>
                {weatherData && <p className="text-slate-300 text-[10px] mb-1">{weatherData.temp} — {weatherData.desc}</p>}
                <div className="flex gap-1">
                  <Button size="sm" onClick={() => { setShowWeather(true); sendDirectorCommand(`WEATHER:ON:${weatherData?.temp}:${weatherData?.desc}`); }} className="flex-1 bg-awareness-blue text-white text-[10px]">Show</Button>
                  <Button size="sm" variant="outline" onClick={() => { setShowWeather(false); sendDirectorCommand("WEATHER:OFF"); }} className="flex-1 border-slate-600 text-slate-300 text-[10px]">Hide</Button>
                </div>
              </div>
            </>)}
          </div>
        </div>

        {/* Center: Preview Monitor — uses the EXACT working ViewerView */}
        <div className="flex-1 p-4 flex flex-col">
          <div className="flex-1 bg-black rounded-xl overflow-hidden relative">
            {/* The working stream viewer — video only, no layout */}
            {podcast.stream_id && activeView === "live" && (
              <div className="absolute inset-0">
                <StreamVideo streamId={podcast.stream_id} />
              </div>
            )}

            {/* Commercial/Break overlays */}
            {activeView === "break" && (
              <div className="absolute inset-0 bg-gradient-to-br from-deep-blue to-[#1a3a5c] flex flex-col items-center justify-center z-10">
                <img src={LOGO_URL} alt="" className="h-20 rounded-lg mb-3" /><p className="text-white font-bold text-xl">Commercial Break</p>
              </div>
            )}
            {activeView === "commercial" && (
              <div className="absolute inset-0 flex items-center justify-center bg-black z-10">
                <div className="text-center"><Video className="w-16 h-16 text-amber-400 mb-2" /><p className="text-white font-bold">Playing: {activeCommercial?.title}</p></div>
              </div>
            )}

            {/* Weather overlay */}
            {showWeather && weatherData && (
              <div className="absolute top-3 left-3 bg-black/70 backdrop-blur-sm text-white px-3 py-2 rounded-lg z-20">
                <p className="text-lg font-bold">{weatherData.temp}</p>
                <p className="text-[10px] text-white/70">{weatherData.desc}</p>
                <p className="text-[10px] text-white/50">{weatherData.city}</p>
              </div>
            )}

            {/* Text overlay (lower third) */}
            {showOverlay && overlayText && (
              <div className="absolute bottom-16 left-0 right-0 z-20">
                <div className="mx-4 bg-deep-blue/90 backdrop-blur-sm border-l-4 border-awareness-blue px-4 py-2">
                  <p className="text-white font-bold text-sm">{overlayText}</p>
                </div>
              </div>
            )}

            {/* Scrolling news bar */}
            {scrollActive && scrollText && (
              <div className="absolute bottom-0 left-0 right-0 bg-red-600 py-1.5 overflow-hidden z-20">
                <div className="flex animate-scroll">
                  <span className="text-white font-bold text-sm whitespace-nowrap px-8">{scrollText}</span>
                  <span className="text-white font-bold text-sm whitespace-nowrap px-8">{scrollText}</span>
                  <span className="text-white font-bold text-sm whitespace-nowrap px-8">{scrollText}</span>
                </div>
              </div>
            )}

            {/* LIVE badge */}
            <div className="absolute top-3 right-3 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1 z-20"><Radio className="w-3 h-3 animate-pulse" /> LIVE</div>
            
            {/* Volume indicator */}
            <div className="absolute bottom-3 right-3 bg-black/60 text-white text-xs px-2 py-1 rounded z-10">Vol: {volume}%</div>
          </div>

          {/* Status bar */}
          <div className="flex items-center justify-between mt-2 text-xs text-slate-400">
            <span>Feed: {activeView.toUpperCase()}</span>
            <span>{showOverlay ? "OVERLAY ON" : ""} {scrollActive ? "| TICKER ON" : ""} {showWeather ? "| WEATHER ON" : ""}</span>
            <span>{viewerCount} viewers</span>
          </div>
        </div>

        {/* Right: Chat */}
        <div className="w-72 bg-slate-800 border-l border-slate-700 flex flex-col shrink-0">
          <div className="p-3 border-b border-slate-700"><h3 className="text-white font-semibold text-sm flex items-center gap-2"><MessageCircle className="w-4 h-4" /> Director Chat</h3></div>
          <div className="flex-1 overflow-y-auto p-3 space-y-2">{chatMessages.map((m, i) => <div key={i} className="text-sm"><span className="font-semibold text-awareness-blue">{m.user_name}: </span><span className="text-slate-300">{m.content}</span></div>)}<div ref={chatEndRef} /></div>
          <div className="p-3 border-t border-slate-700 flex gap-2"><Input value={chatInput} onChange={(e) => setChatInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendChat()} placeholder="Director message..." className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-400 text-xs" /><Button size="sm" onClick={sendChat} className="bg-awareness-blue text-white"><Send className="w-4 h-4" /></Button></div>
        </div>
      </div>
    </div>
  );
}

// Podcast Viewer with sharing
function PodcastViewer({ podcast, user }) {
  const shareUrl = `${window.location.origin}/podcast/${podcast.podcast_id}`;
  const shareText = `Watch "${podcast.title}" on Blue vs Blue Podcast!`;

  const share = (platform) => {
    const urls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
      copy: null
    };
    if (platform === "copy") { navigator.clipboard?.writeText(shareUrl); toast.success("Link copied!"); }
    else if (urls[platform]) window.open(urls[platform], "_blank", "width=600,height=400");
  };

  return (
    <Layout>
      <section className="bg-slate-900 min-h-[calc(100vh-64px)]">
        <div className="container-custom py-6 max-w-3xl">
          <div className="text-center mb-6">
            <Headphones className="w-10 h-10 text-awareness-blue mx-auto mb-2" />
            <h1 className="text-white text-2xl font-bold">{podcast.title}</h1>
            <p className="text-slate-400 mt-1">{podcast.topic}</p>
          </div>
          {podcast.stream_id && podcast.status === "live" ? (
            <div className="space-y-3">
              <Button disabled className="bg-slate-600 text-white w-full py-6 text-lg cursor-not-allowed opacity-80" data-testid="podcast-live-coming-soon"><Radio className="w-5 h-5 mr-2 animate-pulse" /> Live Coming Soon</Button>
              {(user?.role === "super_admin" || user?.role === "master_admin" || user?.role === "developer" || user?.role === "admin") && podcast.stream_id && (
                <a href={`/live/${podcast.stream_id}?director=true`} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="w-full text-white border-white/30"><Settings className="w-4 h-4 mr-2" /> Open Director Mode</Button>
                </a>
              )}
            </div>
          ) : podcast.has_recording ? (
            <div className="aspect-video bg-black rounded-xl overflow-hidden">
              <video src={`${API}/podcasts/${podcast.podcast_id}/recording`} controls className="w-full h-full" />
            </div>
          ) : podcast.media?.length > 0 ? (
            <MediaDisplay media={podcast.media} className="aspect-video" />
          ) : (
            <div className="aspect-video bg-slate-800 rounded-xl flex items-center justify-center"><p className="text-slate-500">Episode coming soon</p></div>
          )}
          <div className="flex flex-wrap justify-center gap-2 mt-4">
            <Button variant="outline" size="sm" onClick={() => share("facebook")} className="text-white border-slate-600 text-xs"><Facebook className="w-3 h-3 mr-1" /> Share</Button>
            <Button variant="outline" size="sm" onClick={() => share("twitter")} className="text-white border-slate-600 text-xs"><Twitter className="w-3 h-3 mr-1" /> Tweet</Button>
            <Button variant="outline" size="sm" onClick={() => share("linkedin")} className="text-white border-slate-600 text-xs"><Linkedin className="w-3 h-3 mr-1" /> Post</Button>
            <Button variant="outline" size="sm" onClick={() => share("copy")} className="text-white border-slate-600 text-xs"><Copy className="w-3 h-3 mr-1" /> Copy</Button>
          </div>
          {podcast.external_links && Object.entries(podcast.external_links).filter(([,v]) => v).length > 0 && (
            <div className="mt-4 text-center"><span className="text-slate-500 text-xs">Also available on:</span><div className="flex justify-center gap-3 mt-2">
              {Object.entries(podcast.external_links).filter(([,v]) => v).map(([p, url]) => <a key={p} href={url} target="_blank" rel="noopener noreferrer" className="text-awareness-blue text-sm hover:underline">{p}</a>)}
            </div></div>
          )}
        </div>
      </section>
    </Layout>
  );
}

export default function PodcastPage() {
  const { podcastId } = useParams();
  const [searchParams] = useSearchParams();
  const isDirector = searchParams.get("mode") === "director" || window.location.pathname.endsWith("/director");
  const { user } = useAuth();
  const [podcasts, setPodcasts] = useState([]);
  const [activePodcast, setActivePodcast] = useState(null);
  const [localStream, setLocalStream] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", topic: "", scheduled_at: "", media: [] });
  const [creating, setCreating] = useState(false);
  const [commercialUploadOpen, setCommercialUploadOpen] = useState(false);
  const [placeholderOpen, setPlaceholderOpen] = useState(false);
  const cameraRef = useRef(null);
  const isAdmin = user?.role === "super_admin" || user?.role === "master_admin" || user?.role === "developer" || user?.role === "admin";

  useEffect(() => { if (podcastId) fetchPodcast(); else fetchPodcasts(); }, [podcastId]);
  const fetchPodcasts = async () => { try { const r = await axios.get(`${API}/podcasts`); setPodcasts(r.data || []); } catch {} };
  const fetchPodcast = async () => { try { const r = await axios.get(`${API}/podcasts/${podcastId}`); setActivePodcast(r.data); } catch { toast.error("Not found"); } };

  const handleCreate = async () => {
    if (!form.title.trim()) { toast.error("Enter a title"); return; }
    setCreating(true);
    try { await axios.post(`${API}/podcasts`, form, { withCredentials: true }); toast.success("Scheduled!"); setCreateOpen(false); setForm({ title: "", description: "", topic: "", scheduled_at: "", media: [] }); fetchPodcasts(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setCreating(false); }
  };

  const goLive = async (podcast) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" }, audio: true });
      cameraRef.current = stream; setLocalStream(stream);
      const r = await axios.post(`${API}/podcasts/${podcast.podcast_id}/go-live`, {}, { withCredentials: true });
      setActivePodcast({ ...podcast, stream_id: r.data.stream.stream_id, status: "live" });
      window.history.pushState(null, "", `/podcast/${podcast.podcast_id}`);
    } catch (e) { toast.error(e.response?.data?.detail || e.message || "Failed"); }
  };

  const handleEnd = () => {
    if (cameraRef.current) { cameraRef.current.getTracks().forEach(t => t.stop()); cameraRef.current = null; }
    setActivePodcast(null); setLocalStream(null);
    window.history.pushState(null, "", "/podcast"); fetchPodcasts();
  };

  // Director mode — full director dashboard with video
  if (activePodcast?.status === "live" && isAdmin && isDirector && !localStream) {
    return <DirectorOnlyView podcast={activePodcast} user={user} onEnd={handleEnd} />;
  }

  // Studio view for admin broadcasting (has camera)
  if (activePodcast?.status === "live" && localStream && isAdmin) {
    return <PodcastStudio podcast={activePodcast} user={user} localStream={localStream} onEnd={handleEnd} />;
  }

  // Viewer for a specific podcast
  if (activePodcast) {
    return <PodcastViewer podcast={activePodcast} user={user} />;
  }

  const livePodcasts = podcasts.filter(p => p.status === "live");
  const scheduledPodcasts = podcasts.filter(p => p.status === "scheduled");
  const pastPodcasts = podcasts.filter(p => p.status === "ended");

  return (
    <Layout>
      <section className="gradient-hero py-12">
        <div className="container-custom">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-awareness-blue font-semibold text-sm uppercase tracking-wide">Podcast</span>
              <h1 className="text-heading-1 text-white mt-3 mb-3">Blue vs Blue Podcast</h1>
              <p className="text-white/80 text-lg">Live shows, interviews, and conversations about prostate cancer awareness.</p>
            </div>
            {isAdmin && (
              <div className="flex flex-wrap gap-2">
                <Button onClick={() => setCreateOpen(true)} className="bg-awareness-blue text-white"><Plus className="w-4 h-4 mr-1" /> New Episode</Button>
                <Button variant="outline" className="text-white border-white/30 text-xs" onClick={() => setCommercialUploadOpen(true)}><Upload className="w-3 h-3 mr-1" /> Commercials</Button>
                <Button variant="outline" className="text-white border-white/30 text-xs" onClick={() => setPlaceholderOpen(true)}><Video className="w-3 h-3 mr-1" /> Placeholder</Button>
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="section-spacing">
        <div className="container-custom space-y-10">
          {livePodcasts.length > 0 && (
            <div><h2 className="text-lg font-bold text-deep-blue mb-4 flex items-center gap-2"><div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" /> Live Now</h2>
              <div className="grid md:grid-cols-2 gap-6">{livePodcasts.map(p => (
                <div key={p.podcast_id} className="bg-white rounded-xl border-2 border-red-200 overflow-hidden hover:shadow-lg">
                  <Link to={`/podcast/${p.podcast_id}`}>
                    <div className="bg-gradient-to-r from-deep-blue to-[#1a3a5c] p-8 text-center"><Headphones className="w-12 h-12 text-awareness-blue mx-auto mb-2" /><span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded">LIVE</span></div>
                  </Link>
                  <div className="p-4">
                    <h3 className="font-bold text-deep-blue text-lg">{p.title}</h3>
                    <p className="text-sm text-slate-500 mt-1">{p.topic}</p>
                    <div className="flex flex-wrap gap-2 mt-3">
                      <Button size="sm" disabled className="bg-slate-500 text-white cursor-not-allowed opacity-80"><Radio className="w-3 h-3 mr-1" /> Live Coming Soon</Button>
                      {isAdmin && p.stream_id && <a href={`/live/${p.stream_id}?director=true`} target="_blank" rel="noopener noreferrer"><Button size="sm" variant="outline"><Settings className="w-3 h-3 mr-1" /> Director Mode</Button></a>}
                    </div>
                  </div>
                </div>
              ))}</div>
            </div>
          )}
          {scheduledPodcasts.length > 0 && (
            <div><h2 className="text-lg font-bold text-deep-blue mb-4 flex items-center gap-2"><Calendar className="w-5 h-5" /> Upcoming</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">{scheduledPodcasts.map(p => (
                <div key={p.podcast_id} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <div className="aspect-video bg-slate-50 overflow-hidden relative">
                    {p.media?.length > 0 ? <MediaDisplay media={p.media} className="w-full h-full" /> : <div className="w-full h-full flex items-center justify-center"><Headphones className="w-10 h-10 text-slate-400" /></div>}
                    <span className="absolute top-3 left-3 bg-awareness-blue/10 text-awareness-blue text-xs font-bold px-2 py-1 rounded z-10">UPCOMING</span>
                  </div>
                  <div className="p-4"><h3 className="font-bold text-deep-blue">{p.title}</h3><p className="text-sm text-slate-500 mt-1">{p.topic}</p>
                    {p.scheduled_at && <p className="text-xs text-slate-400 mt-2 flex items-center gap-1"><Clock className="w-3 h-3" /> {format(new Date(p.scheduled_at), "MMM d, yyyy h:mm a")}</p>}
                    {p.guests?.length > 0 && <p className="text-xs text-awareness-blue mt-1">{p.guests.length} guest(s)</p>}
                    {isAdmin && <Button onClick={() => goLive(p)} className="mt-3 bg-red-500 text-white w-full" size="sm"><Radio className="w-4 h-4 mr-1 animate-pulse" /> Go Live</Button>}
                  </div>
                </div>
              ))}</div>
            </div>
          )}
          {pastPodcasts.length > 0 && (
            <div><h2 className="text-lg font-bold text-deep-blue mb-4">Past Episodes</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">{pastPodcasts.map(p => (
                <Link key={p.podcast_id} to={`/podcast/${p.podcast_id}`} className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-md">
                  <div className="aspect-video bg-slate-100 overflow-hidden">
                    {p.media?.length > 0 ? <MediaDisplay media={p.media} className="w-full h-full" /> : <div className="w-full h-full flex items-center justify-center"><Headphones className="w-10 h-10 text-slate-400" /></div>}
                  </div>
                  <div className="p-4"><h3 className="font-semibold text-deep-blue">{p.title}</h3><p className="text-sm text-slate-500 mt-1">{p.topic}</p>
                    {p.has_recording && <span className="inline-flex items-center gap-1 mt-2 text-sm text-awareness-blue"><Play className="w-4 h-4" /> Watch Episode</span>}
                  </div>
                </Link>
              ))}</div>
            </div>
          )}
          {podcasts.length === 0 && (
            <div className="rounded-2xl overflow-hidden shadow-xl bg-slate-900 max-w-4xl mx-auto" data-testid="podcast-coming-soon-banner">
              <img
                src="https://customer-assets.emergentagent.com/job_6cd70ab0-164b-4026-90ab-4bbce9b2006d/artifacts/1d1dmhr9_7b4880bd-215f-4482-b07d-70926a1a0cdc.png"
                alt="Blue vs Blue Podcast — Coming Soon"
                loading="eager"
                className="w-full h-auto block"
              />
            </div>
          )}
        </div>
      </section>

      {/* Create Episode */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-lg"><DialogHeader><DialogTitle>Schedule New Episode</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Title *</Label><Input value={form.title} onChange={(e) => setForm(p => ({...p, title: e.target.value}))} placeholder="Episode title" /></div>
            <div><Label>Topic</Label><Input value={form.topic} onChange={(e) => setForm(p => ({...p, topic: e.target.value}))} placeholder="Topic" /></div>
            <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm(p => ({...p, description: e.target.value}))} rows={3} /></div>
            <div><Label>Scheduled Date</Label><Input type="datetime-local" value={form.scheduled_at} onChange={(e) => setForm(p => ({...p, scheduled_at: e.target.value}))} /></div>
            <MediaUploader value={form.media} onChange={(m) => setForm(p => ({...p, media: m}))} label="Cover Media (photos/video)" />
            <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button><Button onClick={handleCreate} disabled={creating} className="bg-deep-blue text-white">{creating ? "Creating..." : "Schedule"}</Button></div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Upload Commercial */}
      <Dialog open={commercialUploadOpen} onOpenChange={setCommercialUploadOpen}>
        <DialogContent className="sm:max-w-md"><DialogHeader><DialogTitle>Manage Commercials</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <form onSubmit={async (e) => {
              e.preventDefault();
              const fd = new FormData(e.target);
              try { await axios.post(`${API}/podcasts/commercials`, fd, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } }); toast.success("Commercial uploaded!"); e.target.reset(); } catch { toast.error("Failed"); }
            }}>
              <div className="space-y-3">
                <div><Label>Title</Label><Input name="title" placeholder="Commercial name" /></div>
                <div><Label>Upload Video</Label><input name="file" type="file" accept="video/*" className="block w-full text-sm text-slate-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-deep-blue/10 file:text-deep-blue" /></div>
                <div><Label>Or Video URL</Label><Input name="video_url" placeholder="https://..." /></div>
                <Button type="submit" className="w-full bg-deep-blue text-white"><Upload className="w-4 h-4 mr-1" /> Upload</Button>
              </div>
            </form>
          </div>
        </DialogContent>
      </Dialog>

      {/* Placeholder Video */}
      <Dialog open={placeholderOpen} onOpenChange={setPlaceholderOpen}>
        <DialogContent className="sm:max-w-md"><DialogHeader><DialogTitle>Homepage Placeholder Video</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500 mb-3">This video plays on the homepage podcast section when no live podcast is running.</p>
          <form onSubmit={async (e) => {
            e.preventDefault();
            const fd = new FormData(e.target);
            try { await axios.post(`${API}/podcasts/placeholder-video`, fd, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } }); toast.success("Placeholder video saved!"); setPlaceholderOpen(false); } catch { toast.error("Failed"); }
          }}>
            <div className="space-y-3">
              <div><Label>Upload Video</Label><input name="file" type="file" accept="video/*" className="block w-full text-sm text-slate-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-deep-blue/10 file:text-deep-blue" /></div>
              <div><Label>Or Video URL</Label><Input name="video_url" placeholder="https://youtube.com/..." /></div>
              <Button type="submit" className="w-full bg-deep-blue text-white">Save Placeholder</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
